import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { AuthService } from '../../../services/auth.service';
import { useToast } from '../../../context/ToastContext';
import { CommercialReportService } from '../../../services/commercialReport.service';
import { IExportReceivableReport, IExportReceivableSubtotal } from '../../../interfaces/commercial.interface';
import { money } from '../../statutoryRegisters/shared';
import { formatDate } from '../../../utils/dateUtils';
import { enumLabel } from '../../../utils/garmentsLabels';

const SubtotalTable: React.FC<{ title: string; keyLabel: string; rows: IExportReceivableSubtotal[]; testId: string }> = (
    { title, keyLabel, rows, testId },
) => (
    <TableContainer data-help-section="commercialReports.receivables.subtotals" component={Paper} sx={{ flex: '1 1 320px', maxWidth: 520 }} data-testid={testId}>
        <Typography variant="subtitle2" sx={{ p: 1 }}>{title}</Typography>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell data-help="commercialReports.receivables.col.group">{keyLabel}</TableCell>
                    <TableCell data-help="commercialReports.receivables.col.exps" align="right">EXPs</TableCell>
                    <TableCell data-help="commercialReports.receivables.col.openBdt" align="right">Open (BDT)</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.length === 0 && (
                    <TableRow><TableCell colSpan={3} align="center"><Typography color="text.secondary">No open receivables.</Typography></TableCell></TableRow>
                )}
                {rows.map(r => (
                    <TableRow key={r.key}>
                        <TableCell>{r.key}</TableCell>
                        <TableCell align="right">{r.count}</TableCell>
                        <TableCell align="right">{money(r.openBase)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

/**
 * Export receivables: the receivable booked to the ledger at shipment (EXP form), still open after
 * realisations, per EXP in foreign currency and in BDT at the booked rate, aged from the shipment
 * date with the same buckets as the unrealized-EXP aging. EXPs without a booked receivable (older
 * documents, or booking pending configuration) are not listed here; they stay in the unrealized-EXP
 * aging report.
 */
const ExportReceivablesReport: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [report, setReport] = useState<IExportReceivableReport | null>(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            setReport(await CommercialReportService.getExportReceivableReport(institutionId));
        } catch (e) {
            setReport(null);
            toast.showApiError(e, 'Failed to load export receivables');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    return (
        <Box data-help-page="commercialReports.receivables.page" sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Typography variant="h5">Export Receivables</Typography>
                <Button data-help="commercialReports.receivables.refresh" variant="outlined" size="small" onClick={load} disabled={loading}>Refresh</Button>
            </Box>

            {loading && <CircularProgress size={24} />}

            {!loading && report && (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                        <Chip color="primary" label={`Total open: BDT ${money(report.totalOpenBase)}`} data-testid="export-receivable-total" />
                        <Typography variant="caption" color="text.secondary">
                            Booked at the shipment rate. Foreign amounts are per EXP; BDT is the common unit across currencies.
                        </Typography>
                    </Box>

                    {report.legacyUnbookedCount > 0 && (
                        <Alert severity="info" sx={{ mb: 2 }} data-testid="export-receivable-legacy-note">
                            {report.legacyUnbookedCount} EXP form(s) have no booked receivable (older documents or
                            booking pending). They are not in this report; see the{' '}
                            <RouterLink to="/commercial/reports/unrealized-exp-aging">Unrealized EXP aging</RouterLink>.
                        </Alert>
                    )}
                    {report.truncated && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Showing {report.rows.length} of {report.totalMatchingRows} EXP forms. Totals include all of them.
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <SubtotalTable title="By age" keyLabel="Bucket (days)" rows={report.byBucket} testId="export-receivable-by-bucket" />
                        <SubtotalTable title="By buyer" keyLabel="Buyer" rows={report.byBuyer} testId="export-receivable-by-buyer" />
                    </Box>

                    <TableContainer data-help-section="commercialReports.receivables.table" component={Paper} data-testid="export-receivable-rows">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="commercialReports.receivables.col.exp">EXP</TableCell>
                                    <TableCell data-help="commercialReports.receivables.col.buyer">Buyer</TableCell>
                                    <TableCell data-help="commercialReports.receivables.col.shipped">Shipped</TableCell>
                                    <TableCell data-help="commercialReports.receivables.col.ccy">Ccy</TableCell>
                                    <TableCell data-help="commercialReports.receivables.col.invoice" align="right">Invoice</TableCell>
                                    <TableCell data-help="commercialReports.receivables.col.realized" align="right">Realized</TableCell>
                                    <TableCell data-help="commercialReports.receivables.col.openFc" align="right">Open (FC)</TableCell>
                                    <TableCell data-help="commercialReports.receivables.col.rate" align="right">Booked rate</TableCell>
                                    <TableCell data-help="commercialReports.receivables.col.openBdt" align="right">Open (BDT)</TableCell>
                                    <TableCell data-help="commercialReports.receivables.col.days" align="right">Days</TableCell>
                                    <TableCell data-help="commercialReports.receivables.col.bucket">Bucket</TableCell>
                                    <TableCell data-help="commercialReports.receivables.col.status">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={12} align="center">
                                            <Typography color="text.secondary">No open export receivables.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {report.rows.map(r => (
                                    <TableRow key={r.expId} hover>
                                        <TableCell><RouterLink data-help="commercialReports.receivables.action.openExp" to={`/commercial/exp-form/${r.expId}`}>{r.expNumber}</RouterLink></TableCell>
                                        <TableCell>{r.buyerName}</TableCell>
                                        <TableCell>{formatDate(r.shipmentDate)}</TableCell>
                                        <TableCell>{r.currencyCode}</TableCell>
                                        <TableCell align="right">{money(r.invoiceValue)}</TableCell>
                                        <TableCell align="right">{money(r.realizedAmount)}</TableCell>
                                        <TableCell align="right" data-testid={`receivable-open-fc-${r.expId}`}>{money(r.openForeign)}</TableCell>
                                        <TableCell align="right">{r.bookingRate}</TableCell>
                                        <TableCell align="right" data-testid={`receivable-open-bdt-${r.expId}`}>{money(r.openBase)}</TableCell>
                                        <TableCell align="right">{r.daysOutstanding}</TableCell>
                                        <TableCell>{r.bucketLabel}</TableCell>
                                        <TableCell>{enumLabel(r.realizationStatus)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {!loading && !report && <Typography color="text.secondary">No data available.</Typography>}
        </Box>
    );
};

export default ExportReceivablesReport;
